import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, CircularProgress, Card, CardContent, Stack, Tooltip, IconButton,
  Chip, Collapse, Alert, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Fade, Zoom, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, Tabs, Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Search as SearchIcon,
  Refresh as RefreshIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  PlayArrow as PostIcon, Undo as UnpostIcon, SelectAll as SelectAllIcon,
  Deselect as DeselectIcon, Close as CloseIcon, AssignmentTurnedIn as PostedIcon,
  MenuBook as MenuBookIcon, Payment as PaymentIcon, AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon, Groups as GroupsIcon, PointOfSale as PointOfSaleIcon,
  ReceiptLong as ReceiptLongIcon, AssignmentReturn as AssignmentReturnIcon,
  Output as OutputIcon, Tune as TuneIcon, Input as InputIcon, Inventory as InventoryIcon,
  SwapHoriz as SwapHorizIcon, SettingsSuggest as SettingsSuggestIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon, DoneAll as DoneAllIcon,
  ErrorOutline as ErrorOutlineIcon, Info as InfoIcon, WarningAmber as WarningAmberIcon,
  FilterList as FilterListIcon, ClearAll as ClearAllIcon, DateRange as DateRangeIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { toast } from 'react-toastify';
import {
  getAllUnposted, getAllPosted, batchPost, batchUnpost,
  BatchPostingPageDto, UnpostedSectionDto, UnpostedItemDto, BatchPostResultDto
} from '../services/batchPostingService';

// ==================== Translations ====================
const translations: Record<string, Record<string, string>> = {
  en: {
    title: 'Batch Posting', subtitle: 'Post and unpost all documents from one place',
    totalUnposted: 'Total Unposted', sections: 'Sections', totalAmount: 'Total Amount',
    posted: 'Posted', loading: 'Loading...', noData: 'No unposted documents found',
    refresh: 'Refresh', search: 'Search documents...', postSelected: 'Post Selected',
    unpostSelected: 'Unpost Selected', selectAll: 'Select All', deselectAll: 'Deselect All',
    documentNumber: 'Document #', date: 'Date', description: 'Description', amount: 'Amount',
    status: 'Status', debit: 'Debit', credit: 'Credit', branch: 'Branch', supplier: 'Supplier',
    warehouse: 'Warehouse', createdDate: 'Created', postingResults: 'Posting Results',
    unpostingResults: 'Unposting Results', success: 'Success', failed: 'Failed',
    total: 'Total', close: 'Close', confirmPost: 'Are you sure you want to post',
    confirmUnpost: 'Are you sure you want to unpost', documents: 'documents',
    confirmUnpostTitle: 'Confirm Unpost',
    postingInProgress: 'Posting in progress...', unpostingInProgress: 'Unposting in progress...',
    allPostedSuccess: 'All documents posted successfully!', allUnpostedSuccess: 'All documents unposted successfully!',
    somePostsFailed: 'Some documents failed to post', someUnpostsFailed: 'Some documents failed to unpost',
    noSelection: 'Please select at least one document', expandAll: 'Expand All', collapseAll: 'Collapse All',
    items: 'items', payee: 'Payee', sourceType: 'Source', selected: 'Selected',
    postSuccess: 'Posted successfully', postFailed: 'Failed to post', postError: 'Error during posting',
    unpostSuccess: 'Unposted successfully', unpostFailed: 'Failed to unpost', unpostError: 'Error during unposting',
    dateFrom: 'From Date', dateTo: 'To Date', sectionFilterLabel: 'Section', allSections: 'All Sections', clearFilters: 'Clear Filters',
    tabUnposted: 'Unposted', tabPosted: 'Posted'
  },
  ar: {
    title: 'الترحيل الجماعي', subtitle: 'ترحيل وإلغاء ترحيل جميع المستندات من مكان واحد',
    totalUnposted: 'إجمالي الغير مرحل', sections: 'الأقسام', totalAmount: 'إجمالي المبلغ',
    posted: 'تم الترحيل', loading: 'جاري التحميل...', noData: 'لا توجد مستندات غير مرحلة',
    refresh: 'تحديث', search: 'البحث في المستندات...', postSelected: 'ترحيل المحدد',
    unpostSelected: 'إلغاء ترحيل المحدد', selectAll: 'تحديد الكل', deselectAll: 'إلغاء التحديد',
    documentNumber: 'رقم المستند', date: 'التاريخ', description: 'الوصف', amount: 'المبلغ',
    status: 'الحالة', debit: 'مدين', credit: 'دائن', branch: 'الفرع', supplier: 'المورد',
    warehouse: 'المخزن', createdDate: 'تاريخ الإنشاء', postingResults: 'نتائج الترحيل',
    unpostingResults: 'نتائج إلغاء الترحيل', success: 'نجح', failed: 'فشل',
    total: 'الإجمالي', close: 'إغلاق', confirmPost: 'هل أنت متأكد من ترحيل',
    confirmUnpost: 'هل أنت متأكد من إلغاء ترحيل', documents: 'مستند',
    confirmUnpostTitle: 'تأكيد إلغاء الترحيل',
    postingInProgress: 'جاري الترحيل...', unpostingInProgress: 'جاري إلغاء الترحيل...',
    allPostedSuccess: 'تم ترحيل جميع المستندات بنجاح!', allUnpostedSuccess: 'تم إلغاء ترحيل جميع المستندات بنجاح!',
    somePostsFailed: 'فشل ترحيل بعض المستندات', someUnpostsFailed: 'فشل إلغاء ترحيل بعض المستندات',
    noSelection: 'يرجى تحديد مستند واحد على الأقل', expandAll: 'توسيع الكل', collapseAll: 'طي الكل',
    items: 'عنصر', payee: 'المستفيد', sourceType: 'المصدر', selected: 'محدد',
    postSuccess: 'تم الترحيل بنجاح', postFailed: 'فشل الترحيل', postError: 'خطأ أثناء الترحيل',
    unpostSuccess: 'تم إلغاء الترحيل بنجاح', unpostFailed: 'فشل إلغاء الترحيل', unpostError: 'خطأ أثناء إلغاء الترحيل',
    dateFrom: 'من تاريخ', dateTo: 'إلى تاريخ', sectionFilterLabel: 'القسم', allSections: 'جميع الأقسام', clearFilters: 'مسح الفلترة',
    tabUnposted: 'غير مرحل', tabPosted: 'مرحل'
  }
};

// ==================== Icon Mapper ====================
const getIconByName = (iconName: string): React.ReactElement => {
  const iconMap: Record<string, React.ReactElement> = {
    MenuBookOutlined: <MenuBookIcon />, PaymentOutlined: <PaymentIcon />,
    AccountBalanceOutlined: <AccountBalanceIcon />, ReceiptOutlined: <ReceiptIcon />,
    AccountBalanceWalletOutlined: <AccountBalanceWalletIcon />, GroupsOutlined: <GroupsIcon />,
    PointOfSaleOutlined: <PointOfSaleIcon />, ReceiptLongOutlined: <ReceiptLongIcon />,
    AssignmentReturnOutlined: <AssignmentReturnIcon />, OutputOutlined: <OutputIcon />,
    TuneOutlined: <TuneIcon />, InputOutlined: <InputIcon />, InventoryOutlined: <InventoryIcon />,
    SwapHorizOutlined: <SwapHorizIcon />, SettingsSuggestOutlined: <SettingsSuggestIcon />,
  };
  return iconMap[iconName] || <ReceiptIcon />;
};

// ==================== Section Gradient ====================
const getSectionGradient = (index: number): string => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)', 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
    'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)', 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
    'linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)', 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
  ];
  return gradients[index % gradients.length];
};

// ==================== Custom Countora Checkbox ====================
const CountoraCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  onChange?: () => void;
}> = ({ checked, indeterminate = false, onChange }) => {
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); onChange?.(); }}
      sx={{
        width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        background: checked
          ? 'linear-gradient(135deg, #00c853 0%, #00e676 50%, #69f0ae 100%)'
          : indeterminate
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'transparent',
        border: checked || indeterminate
          ? '2px solid transparent'
          : '2.5px solid rgba(160,160,180,0.45)',
        boxShadow: checked
          ? '0 2px 12px rgba(0,200,83,0.4), 0 0 0 3px rgba(0,200,83,0.12)'
          : indeterminate
            ? '0 2px 12px rgba(102,126,234,0.4), 0 0 0 3px rgba(102,126,234,0.12)'
            : 'none',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: checked || indeterminate ? 'scale(1)' : 'scale(0.88)',
        '&:hover': {
          transform: 'scale(1.12)',
          border: checked || indeterminate
            ? '2px solid transparent'
            : '2.5px solid #00c853',
          boxShadow: checked
            ? '0 4px 20px rgba(0,200,83,0.5), 0 0 0 5px rgba(0,200,83,0.1)'
            : indeterminate
              ? '0 4px 20px rgba(102,126,234,0.5), 0 0 0 5px rgba(102,126,234,0.1)'
              : '0 0 0 5px rgba(0,200,83,0.08)',
        },
        '&:active': { transform: 'scale(0.9)' },
      }}
    >
      {checked && (
        <svg width={14} height={14} viewBox="0 0 16 16" fill="none"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}>
          <path d="M3 8.5L6.5 12L13 4" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
            style={{ strokeDasharray: 22, strokeDashoffset: 0, animation: 'countora-check 0.35s ease-out forwards' }} />
        </svg>
      )}
      {indeterminate && !checked && (
        <Box sx={{ width: 12, height: 2.5, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
      )}
      {!checked && !indeterminate && (
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', transition: 'all 0.2s ease',
          background: 'transparent',
          '.MuiTableRow-root:hover &': { background: 'rgba(0,200,83,0.1)' }
        }} />
      )}
    </Box>
  );
};

const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes countora-check {
    from { stroke-dashoffset: 22; opacity: 0; }
    50% { opacity: 1; }
    to { stroke-dashoffset: 0; opacity: 1; }
  }
`;
if (!document.querySelector('[data-countora-cb]')) {
  styleTag.setAttribute('data-countora-cb', 'true');
  document.head.appendChild(styleTag);
}

// ==================== Main Component ====================
const BatchPostingPage: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const { minDate, maxDate } = useFiscalDateRange();
  const t = translations[language] || translations['en'];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';

  // State
  const [activeTab, setActiveTab] = useState<'unposted' | 'posted'>('unposted');
  const [pageData, setPageData] = useState<BatchPostingPageDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Record<string, Set<number>>>({});
  const [resultDialog, setResultDialog] = useState<{ open: boolean; result: BatchPostResultDto | null; isPost: boolean }>({ open: false, result: null, isPost: true });

  // Load Data
  const loadData = useCallback(async (tab?: 'unposted' | 'posted') => {
    const currentTab = tab || activeTab;
    setLoading(true);
    try {
      const data = currentTab === 'posted' ? await getAllPosted() : await getAllUnposted();
      setPageData(data);
      const allSections = new Set(data.sections.map((s: UnpostedSectionDto) => s.sectionType));
      setExpandedSections(allSections);
      setSelectedItems({});
    } catch (err) { console.error('Error loading batch posting data:', err); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: 'unposted' | 'posted') => {
    setActiveTab(newValue);
    setSelectedItems({});
    setSearchTerm(''); setDateFrom(''); setDateTo(''); setSectionFilter('all');
    loadData(newValue);
  };

  // Toggle section expand
  const toggleSection = (sectionType: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionType)) next.delete(sectionType); else next.add(sectionType);
      return next;
    });
  };

  // Expand/Collapse all
  const expandAll = () => { if (pageData) setExpandedSections(new Set(pageData.sections.map((s: UnpostedSectionDto) => s.sectionType))); };
  const collapseAll = () => { setExpandedSections(new Set()); };

  // Select/Deselect item
  const toggleItem = (sectionType: string, id: number) => {
    setSelectedItems(prev => {
      const sectionSet = new Set(prev[sectionType] || []);
      if (sectionSet.has(id)) sectionSet.delete(id); else sectionSet.add(id);
      return { ...prev, [sectionType]: sectionSet };
    });
  };

  // Select/Deselect all in section
  const toggleAllInSection = (section: UnpostedSectionDto) => {
    const filtered = getFilteredItems(section.items);
    setSelectedItems(prev => {
      const sectionSet = new Set(prev[section.sectionType] || []);
      const allSelected = filtered.every((item: UnpostedItemDto) => sectionSet.has(item.id));
      if (allSelected) { filtered.forEach((item: UnpostedItemDto) => sectionSet.delete(item.id)); }
      else { filtered.forEach((item: UnpostedItemDto) => sectionSet.add(item.id)); }
      return { ...prev, [section.sectionType]: sectionSet };
    });
  };

  // Filter items by search + date range
  const getFilteredItems = (items: UnpostedItemDto[]): UnpostedItemDto[] => {
    let result = items;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((item: UnpostedItemDto) =>
        item.documentNumber?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.descriptionAr?.toLowerCase().includes(term) ||
        item.supplierName?.toLowerCase().includes(term) ||
        item.warehouseName?.toLowerCase().includes(term) ||
        item.branchName?.toLowerCase().includes(term)
      );
    }
    if (dateFrom) {
      result = result.filter((item: UnpostedItemDto) => item.documentDate >= dateFrom);
    }
    if (dateTo) {
      result = result.filter((item: UnpostedItemDto) => item.documentDate <= dateTo);
    }
    return result;
  };

  // Get filtered sections
  const getFilteredSections = (): UnpostedSectionDto[] => {
    if (!pageData) return [];
    let sections = pageData.sections;
    if (sectionFilter !== 'all') {
      sections = sections.filter((s: UnpostedSectionDto) => s.sectionType === sectionFilter);
    }
    return sections;
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm(''); setDateFrom(''); setDateTo(''); setSectionFilter('all');
  };

  const hasActiveFilters = searchTerm || dateFrom || dateTo || sectionFilter !== 'all';

  // Get total selected count
  const getTotalSelectedCount = (): number => {
    return Object.values(selectedItems).reduce((sum, set) => sum + set.size, 0);
  };

  // Get selected count for a section
  const getSectionSelectedCount = (sectionType: string): number => {
    return selectedItems[sectionType]?.size || 0;
  };

  // Handle batch post
  const handleBatchPost = async (sectionType: string) => {
    const ids = Array.from(selectedItems[sectionType] || []);
    if (ids.length === 0) { toast.info(t.noSelection); return; }

    setProcessing(true);
    try {
      const result = await batchPost({ sectionType, ids });
      setResultDialog({ open: true, result, isPost: true });
      if (result.successCount > 0) toast.success(`${t.postSuccess || 'Posted'}: ${result.successCount}`);
      if (result.failedCount > 0) toast.error(`${t.postFailed || 'Failed'}: ${result.failedCount}`);
      await loadData();
      setSelectedItems(prev => ({ ...prev, [sectionType]: new Set<number>() }));
    } catch (err) { console.error('Batch post error:', err); toast.error(t.postError || 'Error during posting'); }
    finally { setProcessing(false); }
  };

  // Handle batch unpost
  const handleBatchUnpost = async (sectionType: string) => {
    const ids = Array.from(selectedItems[sectionType] || []);
    if (ids.length === 0) { toast.info(t.noSelection); return; }
    setConfirmDialog({
      open: true,
      title: t.confirmUnpostTitle || (language === 'ar' ? 'تأكيد إلغاء الترحيل' : 'Confirm Unpost'),
      message: `${t.confirmUnpost} ${ids.length} ${t.documents}?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setProcessing(true);
        try {
          const result = await batchUnpost({ sectionType, ids });
          setResultDialog({ open: true, result, isPost: false });
          if (result.successCount > 0) toast.success(`${t.unpostSuccess || 'Unposted'}: ${result.successCount}`);
          if (result.failedCount > 0) toast.error(`${t.unpostFailed || 'Failed'}: ${result.failedCount}`);
          // Reload current tab data
          await loadData();
          setSelectedItems(prev => ({ ...prev, [sectionType]: new Set<number>() }));
        } catch (err) { console.error('Batch unpost error:', err); toast.error(t.unpostError || 'Error during unposting'); }
        finally { setProcessing(false); }
      }
    });
  };

  // Format number
  const formatNumber = (num: number): string => num.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const formatDate = (date: string): string => date ? new Date(date).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB') : '';

  // ==================== RENDER ====================
  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Processing overlay */}
      {processing && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#fff', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#fff' }}>{t.postingInProgress}</Typography>
        </Box>
      )}

      {/* Header */}
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1 }}>
            {t.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Fade>

      {/* Tabs: Unposted / Posted */}
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102,126,234,0.2)'}` }}>
        <Tabs value={activeTab} onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': { background: activeTab === 'unposted' ? 'linear-gradient(135deg, #eb3349, #f45c43)' : 'linear-gradient(135deg, #11998e, #38ef7d)', height: 3, borderRadius: 2 },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', minHeight: 52 },
            '& .Mui-selected': { color: activeTab === 'unposted' ? '#eb3349 !important' : '#11998e !important' },
          }}>
          <Tab value="unposted" icon={<ErrorOutlineIcon sx={{ fontSize: 20 }} />} iconPosition="start"
            label={`${t.tabUnposted} ${pageData ? `(${pageData.totalUnpostedCount})` : ''}`} />
          <Tab value="posted" icon={<PostedIcon sx={{ fontSize: 20 }} />} iconPosition="start"
            label={t.tabPosted} />
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      {pageData && (() => {
        const fSections = getFilteredSections();
        const fTotalItems = fSections.reduce((sum: number, s: UnpostedSectionDto) => sum + getFilteredItems(s.items).length, 0);
        const fTotalAmount = fSections.reduce((sum: number, s: UnpostedSectionDto) => sum + getFilteredItems(s.items).reduce((a: number, i: UnpostedItemDto) => a + (i.amount || 0), 0), 0);
        return (
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {[
            { value: fTotalItems, label: t.totalUnposted, icon: <ErrorOutlineIcon />, gradient: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' },
            { value: fSections.length, label: t.sections, icon: <InfoIcon />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
            { value: formatNumber(fTotalAmount), label: t.totalAmount, icon: <AccountBalanceWalletIcon />, gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)' },
            { value: getTotalSelectedCount(), label: t.selected, icon: <SelectAllIcon />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
          ].map((stat, index) => (
            <Zoom in timeout={300 + index * 100} key={stat.label}>
              <Card elevation={0} sx={{ flex: '1 1 200px', minWidth: 180, background: stat.gradient, color: 'white', borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' } }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{stat.value}</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{stat.label}</Typography>
                    </Box>
                    <Box sx={{ opacity: 0.3, transform: 'scale(2)' }}>{stat.icon}</Box>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          ))}
        </Box>
        );
      })()}

      {/* Toolbar & Filters */}
      <Fade in timeout={700}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102, 126, 234, 0.2)'}` }}>
          {/* Row 1: Search + Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#667eea' }} /></InputAdornment> }} />
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.expandAll} arrow>
                <IconButton onClick={expandAll} sx={{ bgcolor: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.3)' } }}>
                  <ExpandMoreIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.collapseAll} arrow>
                <IconButton onClick={collapseAll} sx={{ bgcolor: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.3)' } }}>
                  <ExpandLessIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.refresh} arrow>
                <IconButton onClick={() => loadData()} sx={{ bgcolor: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.3)', transform: 'rotate(180deg)' }, transition: 'all 0.3s ease' }}>
                  <RefreshIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Row 2: Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
            <FilterListIcon sx={{ color: '#667eea', fontSize: 22 }} />

            {/* Section Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: 13 }}>{t.sectionFilterLabel}</InputLabel>
              <Select value={sectionFilter} label={t.sectionFilterLabel} onChange={(e) => setSectionFilter(e.target.value)}
                sx={{ borderRadius: 2, fontSize: 13, '& .MuiSelect-select': { py: '8px' } }}>
                <MenuItem value="all">{t.allSections}</MenuItem>
                {pageData?.sections.map((s: UnpostedSectionDto) => (
                  <MenuItem key={s.sectionType} value={s.sectionType}>
                    {language === 'ar' ? s.sectionTitleAr : s.sectionTitle} ({s.unpostedCount})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date From */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.dateFrom}
                value={dateFrom ? new Date(dateFrom) : null}
                onChange={(date) => {
                  const val = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setDateFrom(val);
                  if (val && dateTo && dateTo < val) setDateTo('');
                }}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={dateTo ? new Date(dateTo) : maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } } } }}
              />
            </LocalizationProvider>

            {/* Date To */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.dateTo}
                value={dateTo ? new Date(dateTo) : null}
                onChange={(date) => {
                  const val = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setDateTo(val);
                  if (val && dateFrom && dateFrom > val) setDateFrom('');
                }}
                format="dd/MM/yyyy"
                minDate={dateFrom ? new Date(dateFrom) : minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } } } }}
              />
            </LocalizationProvider>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Tooltip title={t.clearFilters} arrow>
                <Button onClick={clearFilters} size="small" startIcon={<ClearAllIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#f44336',
                    border: '1px solid rgba(244,67,54,0.3)', px: 2,
                    '&:hover': { bgcolor: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.5)' } }}>
                  {t.clearFilters}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Fade>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#667eea' }} />
        </Box>
      )}

      {/* No Data */}
      {!loading && pageData && getFilteredSections().length === 0 && (
        <Fade in timeout={500}>
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
            <PostedIcon sx={{ fontSize: 80, color: '#43e97b', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="text.secondary">{t.noData}</Typography>
          </Paper>
        </Fade>
      )}

      {/* Sections */}
      {!loading && pageData && getFilteredSections().map((section: UnpostedSectionDto, sIndex: number) => {
        const filteredItems = getFilteredItems(section.items);
        const sectionSelected = getSectionSelectedCount(section.sectionType);
        const allSelected = filteredItems.length > 0 && filteredItems.every((item: UnpostedItemDto) => selectedItems[section.sectionType]?.has(item.id));
        if (filteredItems.length === 0 && hasActiveFilters) return null;

        return (
          <Fade in timeout={500 + sIndex * 100} key={section.sectionType}>
            <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, transition: 'all 0.3s ease', '&:hover': { boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(102,126,234,0.15)' } }}>

              {/* Section Header */}
              <Box onClick={() => toggleSection(section.sectionType)} sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(102,126,234,0.04)' }, transition: 'all 0.2s ease' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, background: getSectionGradient(sIndex), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    {getIconByName(section.iconName)}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {isRTL ? section.sectionTitleAr : section.sectionTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {section.unpostedCount} {t.items} • {formatNumber(section.totalAmount)} KWD
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={section.unpostedCount} size="small" sx={{ background: getSectionGradient(sIndex), color: '#fff', fontWeight: 'bold' }} />
                  {sectionSelected > 0 && (
                    <Chip label={`${sectionSelected} ${t.selected}`} size="small" color="primary" variant="outlined" />
                  )}
                  {expandedSections.has(section.sectionType) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
              </Box>

              {/* Section Content */}
              <Collapse in={expandedSections.has(section.sectionType)}>
                {/* Section Actions */}
                <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, pt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CountoraCheckbox checked={allSelected} indeterminate={sectionSelected > 0 && !allSelected} onChange={() => toggleAllInSection(section)} />
                    <Typography variant="body2" color="text.secondary">{allSelected ? t.deselectAll : t.selectAll}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    {activeTab === 'unposted' && (
                    <Button size="small" variant="contained" startIcon={<PostIcon />} disabled={sectionSelected === 0 || processing}
                      onClick={(e) => { e.stopPropagation(); handleBatchPost(section.sectionType); }}
                      sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', borderRadius: 2, textTransform: 'none', boxShadow: '0 3px 10px rgba(17,153,142,0.3)', '&:hover': { boxShadow: '0 5px 15px rgba(17,153,142,0.5)' }, '&:disabled': { background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)' } }}>
                      {t.postSelected} {sectionSelected > 0 && `(${sectionSelected})`}
                    </Button>
                    )}
                    {activeTab === 'posted' && (
                    <Button size="small" variant="outlined" startIcon={<UnpostIcon />} disabled={sectionSelected === 0 || processing}
                      onClick={(e) => { e.stopPropagation(); handleBatchUnpost(section.sectionType); }}
                      sx={{ borderColor: '#f45c43', color: '#f45c43', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: 'rgba(244,92,67,0.08)', borderColor: '#eb3349' } }}>
                      {t.unpostSelected} {sectionSelected > 0 && `(${sectionSelected})`}
                    </Button>
                    )}
                  </Stack>
                </Box>

                {/* Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(102,126,234,0.06)' }}>
                        <TableCell padding="checkbox" sx={{ width: 42 }} />
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.documentNumber}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.date}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.description}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.amount}</TableCell>
                        {section.items.some((i: UnpostedItemDto) => i.branchName) && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.branch}</TableCell>}
                        {section.items.some((i: UnpostedItemDto) => i.supplierName) && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.supplier}</TableCell>}
                        {section.items.some((i: UnpostedItemDto) => i.warehouseName) && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.warehouse}</TableCell>}
                        {section.items.some((i: UnpostedItemDto) => i.payeeName) && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.payee}</TableCell>}
                        {section.items.some((i: UnpostedItemDto) => i.sourceType) && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.sourceType}</TableCell>}
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{t.status}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.map((item: UnpostedItemDto) => {
                        const isSelected = selectedItems[section.sectionType]?.has(item.id) || false;
                        return (
                          <TableRow key={item.id} hover selected={isSelected} onClick={() => toggleItem(section.sectionType, item.id)}
                            sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: isDark ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.08)' }, transition: 'all 0.15s ease' }}>
                            <TableCell padding="checkbox">
                              <CountoraCheckbox checked={isSelected} onChange={() => toggleItem(section.sectionType, item.id)} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="600" sx={{ color: '#667eea' }}>{item.documentNumber}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(item.documentDate)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {isRTL ? item.descriptionAr || item.description : item.description || item.descriptionAr}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="600">{item.amount > 0 ? formatNumber(item.amount) : '-'}</Typography>
                            </TableCell>
                            {section.items.some((i: UnpostedItemDto) => i.branchName) && (
                              <TableCell><Typography variant="body2">{isRTL ? item.branchNameAr || item.branchName : item.branchName}</Typography></TableCell>
                            )}
                            {section.items.some((i: UnpostedItemDto) => i.supplierName) && (
                              <TableCell><Typography variant="body2">{item.supplierName}</Typography></TableCell>
                            )}
                            {section.items.some((i: UnpostedItemDto) => i.warehouseName) && (
                              <TableCell><Typography variant="body2">{isRTL ? item.warehouseNameAr || item.warehouseName : item.warehouseName}</Typography></TableCell>
                            )}
                            {section.items.some((i: UnpostedItemDto) => i.payeeName) && (
                              <TableCell><Typography variant="body2" component="div">{item.payeeName}</Typography></TableCell>
                            )}
                            {section.items.some((i: UnpostedItemDto) => i.sourceType) && (
                              <TableCell><Chip label={item.sourceType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                            )}
                            <TableCell>
                              <Chip label={item.status} size="small" sx={{ fontSize: '0.7rem', bgcolor: item.status === 'Draft' ? (isDark ? 'rgba(255,167,38,0.2)' : 'rgba(255,167,38,0.15)') : (isDark ? 'rgba(102,126,234,0.2)' : 'rgba(102,126,234,0.15)'), color: item.status === 'Draft' ? '#ffa726' : '#667eea' }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          </Fade>
        );
      })}

      {/* Results Dialog */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog({ open: false, result: null, isPost: true })} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e2f' : '#fff' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {resultDialog.result && resultDialog.result.failedCount === 0 ? <DoneAllIcon sx={{ color: '#43e97b' }} /> : <ErrorOutlineIcon sx={{ color: '#f45c43' }} />}
            <Typography variant="h6" fontWeight="bold">{resultDialog.isPost ? t.postingResults : t.unpostingResults}</Typography>
          </Box>
          <IconButton onClick={() => setResultDialog({ open: false, result: null, isPost: true })} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {resultDialog.result && (
            <Box>
              {/* Summary */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Card elevation={0} sx={{ flex: 1, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h4" fontWeight="bold">{resultDialog.result.successCount}</Typography>
                    <Typography variant="caption">{t.success}</Typography>
                  </CardContent>
                </Card>
                <Card elevation={0} sx={{ flex: 1, background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: '#fff', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h4" fontWeight="bold">{resultDialog.result.failedCount}</Typography>
                    <Typography variant="caption">{t.failed}</Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Alert */}
              {resultDialog.result.failedCount === 0 ? (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  {resultDialog.isPost ? t.allPostedSuccess : t.allUnpostedSuccess}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  {resultDialog.isPost ? t.somePostsFailed : t.someUnpostsFailed}
                </Alert>
              )}

              {/* Details - only show failed ones */}
              {resultDialog.result.results.filter(r => !r.isSuccess).length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t.status}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t.description}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultDialog.result.results.filter(r => !r.isSuccess).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.documentNumber || r.id}</TableCell>
                          <TableCell><CancelIcon sx={{ color: '#f45c43', fontSize: 18 }} /></TableCell>
                          <TableCell><Typography variant="body2" color="error">{r.message}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResultDialog({ open: false, result: null, isPost: true })} variant="contained"
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 2, textTransform: 'none' }}>
            {t.close}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' } }}>
        <Box sx={{ textAlign: 'center', pt: 4, pb: 1, px: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(255,152,0,0.35)',
          }}>
            <WarningAmberIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{confirmDialog.title}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{confirmDialog.message}</Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2, px: 3 }}>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            variant="outlined" sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600,
              borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={confirmDialog.onConfirm}
            variant="contained" sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              boxShadow: '0 3px 12px rgba(255,152,0,0.35)',
              '&:hover': { background: 'linear-gradient(135deg, #fb8c00 0%, #ef6c00 100%)', boxShadow: '0 4px 16px rgba(255,152,0,0.45)' } }}>
            {language === 'ar' ? 'تأكيد' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BatchPostingPage;